import { useCallback, useEffect, useState } from "react";

interface UsePushReturn {
  readonly permission: NotificationPermission | "unsupported";
  readonly soundEnabled: boolean;
  readonly requestPermission: () => Promise<void>;
  readonly toggleSound: () => void;
}

function getSoundPreference(): boolean {
  try {
    return localStorage.getItem("push-sound") !== "false";
  } catch {
    return true;
  }
}

function setSoundPreference(enabled: boolean): void {
  try {
    localStorage.setItem("push-sound", enabled ? "true" : "false");
  } catch {
    // Storage unavailable
  }
}

export function usePush(): UsePushReturn {
  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >(() => {
    if (typeof Notification === "undefined") return "unsupported";
    return Notification.permission;
  });
  const [soundEnabled, setSoundEnabled] = useState(getSoundPreference);

  // Keep permission state in sync
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    setPermission(Notification.permission);
  }, []);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === "granted" && "serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        // In production: use VAPID public key from server config
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
        });
        // In production: POST subscription to /api/notifications/subscribe
        void subscription;
      }
    } catch {
      // Permission request failed
    }
  }, []);

  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const next = !prev;
      setSoundPreference(next);
      return next;
    });
  }, []);

  return {
    permission,
    soundEnabled,
    requestPermission,
    toggleSound,
  };
}
